import type * as React from "react"
import { useEffect, useState } from "react"

import { cn } from "@/lib/utils"

interface CurrencyInputProps extends Omit<
  React.ComponentProps<"input">,
  "type" | "value" | "onChange"
> {
  value?: number | null
  onChange?: (value: number | null) => void
  currency?: string
}

function CurrencyInput({
  className,
  value,
  onChange,
  currency = "$",
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState<string>("")

  // Format number to currency string
  const formatCurrency = (num: number | null | undefined): string => {
    if (num === null || num === undefined || isNaN(num)) {
      return ""
    }
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  // Parse currency string to number
  const parseCurrency = (str: string): number | null => {
    // Remove currency symbol, commas, and whitespace
    const cleaned = str.replace(/[^\d.-]/g, "")
    if (cleaned === "" || cleaned === "-") {
      return null
    }
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? null : parsed
  }

  // Update display value when prop value changes
  useEffect(() => {
    if (value !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- controlled value is mirrored into the formatted display string
      setDisplayValue(formatCurrency(value))
    }
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Allow empty input
    if (inputValue === "") {
      setDisplayValue("")
      onChange?.(null)
      return
    }

    // Remove currency symbol, commas, and whitespace
    const cleaned = inputValue.replace(/[^\d.-]/g, "")

    // Allow partial input (e.g., "1", "12", "123")
    if (cleaned === "" || cleaned === "-") {
      setDisplayValue(cleaned)
      return
    }

    const numValue = parseFloat(cleaned)
    if (!isNaN(numValue)) {
      // Update the numeric value
      onChange?.(numValue)

      // Keep the cleaned numeric value for display while typing
      setDisplayValue(cleaned)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const numValue = parseCurrency(e.target.value)
    if (numValue !== null) {
      setDisplayValue(formatCurrency(numValue))
    } else {
      setDisplayValue("")
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // On focus, show the raw number without formatting for easier editing
    const numValue = parseCurrency(e.target.value)
    if (numValue !== null) {
      setDisplayValue(numValue.toString())
    }
  }

  return (
    <div className="relative">
      <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
        {currency}
      </span>
      <input
        type="text"
        inputMode="decimal"
        data-slot="input"
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 pl-7 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          className,
        )}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder="0.00"
        {...props}
      />
    </div>
  )
}

export { CurrencyInput }
